import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useCart } from '../lib/cart-store.js';
import { useRecent } from '../lib/recent-store.js';
import { money2 } from '../lib/format.js';
import { IconScan, IconSearch } from '../components/icons.js';
import { QueryError } from '../components/query-error.js';

// Lazy-loaded so @zxing (~400 KB) is a separate chunk, fetched only when a rep
// opens the scanner — keeps it out of the main bundle.
const BarcodeScanner = lazy(() =>
  import('../components/barcode-scanner.js').then((m) => ({ default: m.BarcodeScanner })),
);

const SORTS = [
  { key: 'relevance', label: 'Relevance' },
  { key: 'price', label: 'Price ↑' },
  { key: 'stock', label: 'In stock' },
] as const;
type Sort = (typeof SORTS)[number]['key'];

const PAGE = 25;
const MAX = 500;

export function InventorySearchScreen() {
  const qc = useQueryClient();
  const recentItems = useRecent((s) => s.items);
  const orderCustomer = useCart((s) => s.selectedCustomer);

  const [q, setQ] = useState('');
  const [activeQ, setActiveQ] = useState('');
  const [groupNo, setGroupNo] = useState('');
  const [scan, setScan] = useState(false);
  const [sort, setSort] = useState<Sort>('relevance');
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => {
    const t = setTimeout(() => setActiveQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);
  // Reset the page size whenever the search term or category changes.
  useEffect(() => setLimit(PAGE), [activeQ, groupNo]);

  // Category browse — the product-group roster (cached server-side + here).
  const groups = useQuery({
    queryKey: ['inventory', 'groups'],
    queryFn: ({ signal }) => api.inventory.groups(signal),
    staleTime: 60 * 60_000,
  });

  const hasCriteria = activeQ.length > 0 || groupNo !== '';
  const results = useQuery({
    queryKey: ['inventory', 'search', activeQ, groupNo, limit],
    queryFn: ({ signal }) =>
      api.inventory.search(
        { q: activeQ || undefined, groupNo: groupNo || undefined, limit },
        signal,
      ),
    enabled: hasCriteria,
  });

  const prefetch = (spireId: number) =>
    void qc.prefetchQuery({ queryKey: ['inventory', 'detail', spireId], queryFn: () => api.inventory.getById(spireId) });

  const sorted = useMemo(() => {
    const d = results.data ?? [];
    if (sort === 'relevance') return d;
    const c = [...d];
    if (sort === 'price') c.sort((a, b) => Number(a.defaultSellPrice) - Number(b.defaultSellPrice));
    else c.sort((a, b) => Number(b.availableQty) - Number(a.availableQty));
    return c;
  }, [results.data, sort]);

  const maybeMore = (results.data?.length ?? 0) >= limit && limit < MAX;

  return (
    <>
      <div className="topbar">
        <div>
          <h2>Inventory</h2>
          <div className="sub">Search by part # or SKU</div>
        </div>
      </div>
      {orderCustomer && (
        <div className="order-ctx">
          Ordering for <strong>{orderCustomer.name}</strong> — <Link to="/cart">view cart</Link>
        </div>
      )}
      <div className="search-bar">
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="search-wrap" style={{ flex: 1 }}>
            <IconSearch size={16} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Part # or description (e.g. crown)"
              aria-label="Search inventory by part number"
              autoFocus
            />
          </div>
          <button type="button" className="icon-btn" onClick={() => setScan(true)} title="Scan barcode" aria-label="Scan barcode">
            <IconScan size={18} />
          </button>
        </div>
        <select
          value={groupNo}
          onChange={(e) => setGroupNo(e.target.value)}
          aria-label="Category"
          style={{ marginTop: 8, width: '100%', padding: '9px 12px', border: '1px solid var(--kd-line)', borderRadius: 10, fontSize: 14, background: '#fff', color: groupNo ? 'var(--kd-text)' : 'var(--kd-text-3)' }}
        >
          <option value="">All categories</option>
          {(groups.data ?? []).map((g) => (
            <option key={g.code} value={g.code}>
              {g.description ?? g.code}
            </option>
          ))}
        </select>
      </div>

      {scan && (
        <Suspense fallback={null}>
          <BarcodeScanner
            onDetect={(code) => {
              setQ(code);
              setActiveQ(code);
              setScan(false);
            }}
            onClose={() => setScan(false)}
          />
        </Suspense>
      )}

      {hasCriteria && results.isSuccess && sorted.length > 0 && (
        <div className="filter-chips">
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`chip${sort === s.key ? ' active' : ''}`}
              onClick={() => setSort(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ paddingTop: 8 }}>
        {!hasCriteria &&
          (recentItems.length > 0 ? (
            <>
              <div className="section-title">Recent items</div>
              {recentItems.map((ri) => (
                <Link
                  key={ri.spireId}
                  to="/inventory/$id"
                  params={{ id: String(ri.spireId) }}
                  className="inv-row"
                  onPointerEnter={() => prefetch(ri.spireId)}
                >
                  <div className="inv-thumb">{ri.partNo.slice(0, 3)}</div>
                  <div className="body">
                    <div className="name">{ri.description ?? ri.partNo}</div>
                    <div className="sku">{ri.partNo}</div>
                  </div>
                </Link>
              ))}
            </>
          ) : (
            <div className="kd-muted" style={{ padding: '8px 18px' }}>
              Type a part number, or tap the scan icon.
            </div>
          ))}

        {results.isPending && hasCriteria && Array.from({ length: 5 }).map((_, i) => <div key={i} className="kd-skeleton" />)}
        {results.isError && <QueryError message={results.error.message} onRetry={() => void results.refetch()} />}

        {results.isSuccess && hasCriteria && sorted.length === 0 && (
          <div style={{ padding: '16px 18px', textAlign: 'center' }}>
            <div className="kd-muted" style={{ marginBottom: 10 }}>
              No items match {activeQ ? `"${activeQ}"` : 'this category'}.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button type="button" className="btn btn-secondary" style={{ flex: 'none', padding: '8px 14px' }} onClick={() => setScan(true)}>
                Scan instead
              </button>
              <button type="button" className="btn btn-secondary" style={{ flex: 'none', padding: '8px 14px' }} onClick={() => setQ('')}>
                Clear
              </button>
            </div>
          </div>
        )}

        {sorted.map((item) => {
          const avail = Number(item.availableQty);
          return (
            <Link
              key={`${item.spireId}-${item.whseCode}`}
              to="/inventory/$id"
              params={{ id: String(item.spireId) }}
              className="inv-row"
              onPointerEnter={() => prefetch(item.spireId)}
              onFocus={() => prefetch(item.spireId)}
            >
              <div className="inv-thumb">{item.partNo.slice(0, 3)}</div>
              <div className="body">
                <div className="name">{item.description ?? item.partNo}</div>
                <div className="sku">{item.partNo}</div>
                <div className="price-row">
                  <span className="price">{money2(item.defaultSellPrice)}</span>
                  <span className={`stock-pill ${avail > 0 ? 'in' : 'out'}`}>
                    {avail > 0 ? `${item.availableQty} on hand` : 'Out of stock'}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}

        {maybeMore && (
          <button type="button" className="show-more" onClick={() => setLimit((l) => Math.min(l + PAGE, MAX))}>
            Show more
          </button>
        )}
      </div>
    </>
  );
}
